<div class="client-admin">
  <div class="client-admin__grid">
    <!-- LEFT COLUMN: Admin Tools -->
    <app-display-card [justifyContent]="'start'" class="client-admin__card">
      <header class="client-admin__section-head">
        <h3 class="mb-0">Admin tools</h3>
      </header>

      <form [formGroup]="CompanyForm">
        <dl class="client-admin__tools">
          @for (tool of adminTools; track tool) {
            @if (tool.show) {
              <div class="client-admin__tool">
                <dt class="client-admin__tool-label">
                  <span>{{ tool.label }}</span>
                  @if (tool.helperText) {
                    <span
                      class="client-admin__tool-helper"
                      [id]="tool.label + 'HelperText'"
                      appStatusStyleChanges
                      [textTransform]="'uppercase'"
                      [boldText]="true"
                    >
                      {{ tool.helperText }}
                    </span>
                  }
                </dt>
                <dd class="client-admin__tool-control">
                  @if (tool.inputType === 'checkBox') {
                    <input
                      [id]="tool.label"
                      type="checkbox"
                      class="form-check-input"
                      formControlName="{{ tool.formControl }}"
                    />
                  }
                  @if (tool.inputType === 'button' && tool.buttonFunction) {
                    <button
                      type="button"
                      [id]="tool.label"
                      (click)="tool.buttonFunction()"
                      class="btn btn-outline btn-sm"
                    >
                      Update
                    </button>
                  }
                  @if (tool.inputType === 'select') {
                    <select
                      [id]="tool.label"
                      class="form-select form-select-sm"
                      formControlName="{{ tool.formControl }}"
                    >
                      @for (option of tool.selectOptions; track option) {
                        <option [ngValue]="option.value">
                          {{ option.key }}
                        </option>
                      }
                    </select>
                  }
                </dd>
              </div>
            }
          }
        </dl>
      </form>

      @if (CompanyForm.controls.allowClientRegisteredVendorEmails.value) {
        <div class="action-row justify-end client-admin__cta">
          <button
            type="button"
            class="btn btn-primary btn-sm"
            (click)="sendClientRegisteredVendorEmails()"
          >
            <i class="bi bi-envelope me-1"></i>
            Send invitation emails
          </button>
        </div>
      }
    </app-display-card>

    <!-- RIGHT COLUMN: Stack of operational cards -->
    <div class="client-admin__right">
      <app-display-card [justifyContent]="'start'" class="client-admin__card">
        <header class="client-admin__section-head">
          <h3 class="mb-0">Fee vouchers</h3>
        </header>
        <dl class="client-admin__stats">
          <div class="client-admin__stat">
            <dt>Total</dt>
            <dd>
              {{ Company?.CompanyPackagePricing?.packageWaiverAmount || 0 }}
            </dd>
          </div>
          <div class="client-admin__stat">
            <dt>Vendors waived</dt>
            <dd>{{ Company?.totalVendorsFeeWaived || 0 }}</dd>
          </div>
          <div class="client-admin__stat">
            <dt>Remaining</dt>
            <dd>{{ Company?.feeVouchersLeft || 0 }}</dd>
          </div>
        </dl>
        <div class="action-row justify-end client-admin__cta">
          <button
            type="button"
            class="btn btn-outline btn-sm"
            (click)="updateFeeVouchers()"
          >
            <i class="bi bi-pencil me-1"></i>
            Update vouchers
          </button>
        </div>
      </app-display-card>

      <app-display-card [justifyContent]="'start'" class="client-admin__card">
        <header class="client-admin__section-head">
          <h3 class="mb-0">Blocked vendors</h3>
        </header>
        <dl class="client-admin__stats">
          <div class="client-admin__stat">
            <dt>Blocked Tax IDs</dt>
            <dd>{{ blockedTaxIdsCount }}</dd>
          </div>
        </dl>
        <div class="action-row justify-end client-admin__cta">
          <button
            type="button"
            class="btn btn-outline btn-sm"
            (click)="updateBlockedList()"
          >
            <i class="bi bi-pencil me-1"></i>
            Update blocked list
          </button>
        </div>
      </app-display-card>

      <app-display-card [justifyContent]="'start'" class="client-admin__card">
        <header class="client-admin__section-head">
          <h3 class="mb-0">Client user upload</h3>
        </header>
        <div class="action-row justify-end client-admin__cta">
          <button
            type="button"
            class="btn btn-outline btn-sm"
            (click)="downloadUsersTemplate()"
          >
            <i class="bi bi-download me-1"></i>
            Download template
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            (click)="uploadUsers()"
          >
            <i class="bi bi-upload me-1"></i>
            Upload users
          </button>
        </div>
      </app-display-card>

      <app-display-card [justifyContent]="'start'" class="client-admin__card">
        <header class="client-admin__section-head">
          <h3 class="mb-0">Download files</h3>
        </header>
        <dl class="client-admin__stats">
          <div class="client-admin__stat">
            <dt>Last all-files run</dt>
            <dd>
              {{
                ($safeNavigationMigration(Company?.lastDateRanAllFileDownload)
                  | date) || '—'
              }}
            </dd>
          </div>
        </dl>
        <div class="action-row justify-end client-admin__cta">
          <button
            type="button"
            class="btn btn-outline btn-sm"
            (click)="downloadAllFiles(250, 0, 0)"
          >
            <i class="bi bi-download me-1"></i>
            All files
          </button>
          <button
            type="button"
            class="btn btn-outline btn-sm"
            (click)="downloadVendorAgreementsAndW9(250, 0)"
          >
            <i class="bi bi-download me-1"></i>
            Vendor agreements & W-9s
          </button>
        </div>
      </app-display-card>

      @if (Company?.allowClientRegisteredVendors) {
        <app-display-card [justifyContent]="'start'" class="client-admin__card">
          <header class="client-admin__section-head">
            <h3 class="mb-0">Client registered vendors</h3>
          </header>
          <div class="action-row justify-end client-admin__cta">
            <button
              type="button"
              class="btn btn-outline btn-sm"
              (click)="downloadClientRegisteredVendorUploadTemplate()"
            >
              <i class="bi bi-download me-1"></i>
              Download template
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              (click)="uploadClientRegisteredVendorFile()"
            >
              <i class="bi bi-upload me-1"></i>
              Upload file
            </button>
            @if (
              !Company?.allowAutomatedBusinessVerificationClientRegisteredVendors
            ) {
              <button
                type="button"
                class="btn btn-primary btn-sm"
                (click)="mapApVendorsToClientRegisteredVendors()"
              >
                Create from AP vendors
              </button>
            }
          </div>
        </app-display-card>
      }

      <app-display-card [justifyContent]="'start'" class="client-admin__card">
        <header class="client-admin__section-head">
          <h3 class="mb-0">Client managed AP vendors</h3>
        </header>
        <p class="client-admin__hint">
          Mark many AP vendors as Client Managed at once. Vantaca configurations
          past their v3 cut-over only.
        </p>
        <div class="action-row justify-end client-admin__cta">
          <button
            type="button"
            class="btn btn-outline btn-sm"
            (click)="downloadClientManagedTemplate()"
          >
            <i class="bi bi-download me-1"></i>
            Download template
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            (click)="uploadClientManagedApVendorFile()"
          >
            <i class="bi bi-upload me-1"></i>
            Upload file
          </button>
        </div>
      </app-display-card>

      <app-display-card [justifyContent]="'start'" class="client-admin__card">
        <header class="client-admin__section-head">
          <h3 class="mb-0">Compliance exempt vendors</h3>
        </header>
        <div class="action-row justify-end client-admin__cta">
          <button
            type="button"
            class="btn btn-outline btn-sm"
            (click)="downloadExemptVendorTypes()"
          >
            <i class="bi bi-download me-1"></i>
            Exempt vendor types
          </button>
          <button
            type="button"
            class="btn btn-outline btn-sm"
            (click)="downloadComplianceExemptVendorTemplate()"
          >
            <i class="bi bi-download me-1"></i>
            Download template
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            (click)="uploadComplianceExemptVendorFile()"
          >
            <i class="bi bi-upload me-1"></i>
            Upload file
          </button>
        </div>
        @if (Company?.allowExemptVendors) {
          <div class="action-row justify-end client-admin__cta mt-1">
            <button
              type="button"
              class="btn btn-primary btn-sm"
              (click)="mapApVendorsToExemptVendors()"
            >
              Create from AP vendors
            </button>
          </div>
        }
      </app-display-card>
    </div>
  </div>

  <!-- Full-width company fees -->
  <app-display-card [justifyContent]="'start'" class="client-admin__card">
    <header
      class="client-admin__section-head client-admin__section-head--split"
    >
      <h3 class="mb-0">Company fees</h3>
      @if (Company?.CompanyFees?.length !== 4) {
        <button
          type="button"
          class="btn btn-outline btn-sm"
          (click)="addCompanyFee()"
        >
          <i class="bi bi-plus-lg me-1"></i>
          Add company fee
        </button>
      }
    </header>
    <div class="client-admin__grid-wrap">
      <app-company-fee-grid
        [gridName]="'AdminCompanyFees'"
        [ColumnIdToColumnActions]="ColumnIdToColumnActions"
        [suppliedQueryData]="suppliedQueryData"
        (actionEvent)="action($event)"
        [(reloadGridData)]="reloadGrid"
      ></app-company-fee-grid>
    </div>
  </app-display-card>
</div>
